/**
 * ============================================================================
 * UnivoLink
 * ============================================================================
 *
 * Purpose: Standardized next/link wrapper for internal and external navigation.
 * Responsibility: Link defaults (prefetch, rel, target) without changing routes.
 * Dependencies: next/link
 * Impact Area: Adopt incrementally per component.
 * Safe To Modify: External link detection and default rel/target.
 * Avoid Changing: href contract.
 * Risk Level: Low (opt-in per file)
 * ============================================================================
 */

import Link, { type LinkProps } from "next/link";
import { type AnchorHTMLAttributes, type ReactNode } from "react";

type UnivoLinkBaseProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  children: ReactNode;
  className?: string;
  /** Disable prefetch for heavy or low-priority links. */
  prefetch?: LinkProps["prefetch"];
};

type InternalLinkProps = UnivoLinkBaseProps & {
  href: LinkProps["href"];
  external?: false;
  isAnchor?: boolean;
};

type ExternalLinkProps = UnivoLinkBaseProps & {
  href: string;
  external: true;
  openInNewTab?: boolean;
  isAnchor?: boolean;
};

export type UnivoLinkProps = InternalLinkProps | ExternalLinkProps;

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
}

export default function UnivoLink(props: UnivoLinkProps) {
  const { children, className, prefetch = false, isAnchor = false, ...rest } = props;
  const isExplicitExternal = "external" in props && props.external;

  if (isExplicitExternal || isAnchor) {
    const { href, openInNewTab = isExplicitExternal, external: _external, ...anchorProps } =
      rest as ExternalLinkProps;
    return (
      <a
        href={href}
        className={className}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  const { href, ...anchorProps } = rest as InternalLinkProps;
  const hrefString = typeof href === "string" ? href : "";

  if (hrefString && isExternalHref(hrefString)) {
    return (
      <a
        href={hrefString}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  return (
    <Link href={href} prefetch={prefetch} className={className} {...anchorProps}>
      {children}
    </Link>
  );
}
